import React, { useState, useEffect } from 'react';
import SlideoutPanel from './Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';
import AddPractitionerForm from './Forms/AddPractitionerForm';
import PractitionerScheduleEditor from './Forms/PractitionerScheduleEditor';
import PractitionerCalendarTab from './Forms/PractitionerCalendarTab';
import { BasicInfoStep } from './Forms/PractitionerOnboardingSteps';
import PageHeader from './Common/PageHeader';
import { TableSkeleton, CardSkeleton } from './Common/SkeletonLoader';
import apiService from '../services/api';

// Data transformation functions
const transformApiPractitioner = (apiPractitioner) => {
  // API returns 'locations' array of PractitionerLocationDTO
  const locations = apiPractitioner.locations || [];
  const primaryLocation = locations.length > 0
    ? locations[0].locationName
    : 'Various Locations';

  return {
    id: apiPractitioner.id,
    name: `${apiPractitioner.firstName} ${apiPractitioner.lastName}`,
    fullName: `${apiPractitioner.firstName} ${apiPractitioner.lastName}`,
    firstName: apiPractitioner.firstName,
    lastName: apiPractitioner.lastName,
    email: apiPractitioner.email,
    phone: apiPractitioner.phone || '',
    specialization: apiPractitioner.specialization,
    title: 'Dr.', // Default title
    qualifications: apiPractitioner.qualifications || '',
    registrationNumber: apiPractitioner.registrationNumber || '',
    experience: apiPractitioner.yearsOfExperience ? `${apiPractitioner.yearsOfExperience} years` : 'N/A',
    yearsOfExperience: apiPractitioner.yearsOfExperience || 0,
    location: primaryLocation,
    locationIds: locations.map(l => l.locationId), // Array of assigned location IDs
    bio: apiPractitioner.bio || '',
    status: apiPractitioner.status === 0 ? 'active' : 'inactive', // Map enum to string
    joinDate: apiPractitioner.createdAt ? new Date(apiPractitioner.createdAt).toISOString().split('T')[0] : '',
    hourlyRate: apiPractitioner.hourlyRate || 0,
    languages: apiPractitioner.languages || '',
    offices: locations // keep for backward compat
  };
};

const transformPractitionerToApi = (practitioner, isCreate = false) => {
  // Handle name extraction from fullName or use existing firstName/lastName
  let firstName = '';
  let lastName = '';

  if (practitioner.fullName) {
    const nameParts = practitioner.fullName.trim().split(' ');
    firstName = nameParts[0] || '';
    lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'Unknown'; // Default lastName if not provided
  } else {
    firstName = practitioner.firstName || '';
    lastName = practitioner.lastName || 'Unknown'; // Default lastName if not provided
  }

  const baseData = {
    firstName,
    lastName,
    email: practitioner.email || '',
    phone: practitioner.phone || null,
    specialization: practitioner.specialization || '',
    registrationNumber: practitioner.registrationNumber || null,
    hourlyRate: practitioner.hourlyRate || null,
    yearsOfExperience: practitioner.yearsOfExperience || null,
    bio: practitioner.bio || null,
    qualifications: practitioner.qualifications || null,
    languages: practitioner.languages || null,
    locationIds: practitioner.locationIds && practitioner.locationIds.length > 0
      ? practitioner.locationIds
      : null
  };

  // Only include status for updates, not for creates
  if (!isCreate) {
    baseData.status = practitioner.status === 'active' ? 0 : 1; // Map string to enum
  }

  return baseData;
};


// Tabbed container for edit slideout (Details + Schedule)
const PractitionerEditTabs = ({ practitioner, onSubmit, onCancel, showNotification }) => {
  const [activeTab, setActiveTab] = React.useState('details');

  return (
    <div>
      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: '4px', borderBottom: '1px solid var(--gray-200)',
        marginBottom: '20px', paddingBottom: '0',
      }}>
        {[
          { key: 'details',  label: '👤 Details'  },
          { key: 'schedule', label: '📅 Schedule' },
          { key: 'calendar', label: '🗓️ Calendar' },
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderBottom: activeTab === tab.key
                ? '2px solid var(--primary, #1e3a5f)'
                : '2px solid transparent',
              background: 'none',
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--primary, #1e3a5f)' : 'var(--gray-500)',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '-1px',
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'details' && (
        <AddPractitionerForm
          initialData={practitioner}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isEditing={true}
        />
      )}
      {activeTab === 'schedule' && (
        <PractitionerScheduleEditor
          practitionerId={practitioner.id}
          showNotification={showNotification}
        />
      )}
        {activeTab === 'calendar' && (
          <PractitionerCalendarTab practitioner={practitioner} />
        )}
    </div>
  );
};

const PractitionersView = ({ showNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [editingPractitioner, setEditingPractitioner] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout } = useSlideout();

  // Fetch practitioners from API (with one silent retry for cold-start latency)
  const fetchPractitioners = async () => {
    setLoading(true);
    setApiError(false);
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 2000));
        const response = await apiService.getPractitioners();
        const transformedPractitioners = response.map(transformApiPractitioner);
        setPractitioners(transformedPractitioners);
        setLoading(false);
        return;
      } catch (error) {
        lastError = error;
      }
    }
    setPractitioners([]);
    setApiError(true);
    setLoading(false);
  };

  // Load practitioners on mount
  useEffect(() => {
    fetchPractitioners();
  }, []);

  // Filter practitioners based on search and status
  const filteredPractitioners = practitioners.filter(practitioner => {
    const matchesSearch = searchTerm === '' ||
      practitioner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      practitioner.specialization.toLowerCase().includes(searchTerm.toLowerCase()) ||
      practitioner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      practitioner.qualifications.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || practitioner.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Slideout handlers
  const handleAddPractitioner = () => {
    const formContent = (
      <AddPractitionerForm
        onSubmit={handleCreatePractitioner}
        onCancel={closeSlideout}
      />
    );

    openSlideout('Add New Practitioner', formContent, '750px');
  };

  const handleEditPractitioner = (practitioner) => {
    setEditingPractitioner(practitioner);
    const formContent = (
      <PractitionerEditTabs
        practitioner={practitioner}
        onSubmit={(data) => handleUpdatePractitioner(practitioner.id, data)}
        onCancel={closeSlideout}
        showNotification={showNotification}
      />
    );

    openSlideout('Edit Practitioner', formContent, '800px');
  };

  const handleViewPractitioner = (practitioner) => {
    const detailContent = (
      <PractitionerDetail
        practitioner={practitioner}
        onEdit={() => handleEditPractitioner(practitioner)}
        onToggleStatus={() => handleToggleStatus(practitioner.id)}
      />
    );

    openSlideout(practitioner.name, detailContent, '600px');
  };

  const handleOnboardPractitioner = () => {
    const onboardingContent = (
      <BasicInfoStep
        data={{}}
        onNext={handleOnboardingNext}
        onCancel={closeSlideout}
        isFirstStep={true}
      />
    );

    openSlideout('Onboard New Practitioner', onboardingContent, '750px');
  };

  // Onboarding flow
  const handleOnboardingNext = (stepData) => {
    closeSlideout();
    showNotification('Practitioner onboarding started!', 'success');
  };

  // CRUD operations (using API)
  const handleCreatePractitioner = async (practitionerData) => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const apiData = transformPractitionerToApi(practitionerData, true); // Mark as create
      const response = await apiService.createPractitioner(apiData);
      const newPractitioner = transformApiPractitioner(response);

      setPractitioners(prev => [...prev, newPractitioner]);
      closeSlideout();
      setEditingPractitioner(null);
      showNotification('Practitioner added successfully!', 'success');
    } catch (error) {
      console.error('Error creating practitioner:', error);
      showNotification('Failed to create practitioner', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePractitioner = async (practitionerId, practitionerData) => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const apiData = transformPractitionerToApi(practitionerData);
      const response = await apiService.updatePractitioner(practitionerId, apiData);
      const updatedPractitioner = transformApiPractitioner(response);

      setPractitioners(prev =>
        prev.map(practitioner =>
          practitioner.id === practitionerId ? updatedPractitioner : practitioner
        )
      );

      closeSlideout();
      setEditingPractitioner(null);
      showNotification('Practitioner updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating practitioner:', error);
      showNotification('Failed to update practitioner', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePractitioner = async (practitionerId) => {
    if (submitting) return;

    if (window.confirm('Are you sure you want to delete this practitioner?')) {
      try {
        setSubmitting(true);
        await apiService.deletePractitioner(practitionerId);
        setPractitioners(prev => prev.filter(practitioner => practitioner.id !== practitionerId));
        showNotification('Practitioner deleted successfully!', 'success');
      } catch (error) {
        console.error('Error deleting practitioner:', error);
        showNotification('Failed to delete practitioner', 'error');
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Quick actions
  const handleToggleStatus = async (practitionerId) => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const practitioner = practitioners.find(p => p.id === practitionerId);
      if (!practitioner) return;

      const updatedData = {
        ...transformPractitionerToApi(practitioner),
        status: practitioner.status === 'active' ? 1 : 0 // Toggle status
      };

      const response = await apiService.updatePractitioner(practitionerId, updatedData);
      const updatedPractitioner = transformApiPractitioner(response);

      setPractitioners(prev =>
        prev.map(p => p.id === practitionerId ? updatedPractitioner : p)
      );

      showNotification('Status updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating practitioner status:', error);
      showNotification('Failed to update status', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkAction = (action, selectedIds) => {
    showNotification(`Bulk ${action} applied to ${selectedIds.length} practitioners`, 'success');
  };

  return (
    <div className="practitioners-view">
      <PageHeader pageKey="practitioners">
        <button className="btn btn-primary" onClick={handleAddPractitioner}>
          <span className="icon icon--add icon--success"></span>
          Add New Practitioner
        </button>
      </PageHeader>

      {/* Status Filter */}
      <div className="material-filters-section">
        <div className="material-filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="material-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        <div className="material-table-container">
          <div className="material-table-header">
            <h3 className="material-table-title">Practitioner Overview</h3>
            <span className="material-table-meta">Showing {filteredPractitioners.length} of {practitioners.length} practitioners</span>
          </div>

          <div className="material-table-controls">
            <div className="material-search">
              <input
                type="text"
                className="material-search-input"
                placeholder="Search practitioners..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="material-view-toggle">
              <button
                className={`material-chip ${viewMode === 'table' ? 'selected' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
              <button
                className={`material-chip ${viewMode === 'cards' ? 'selected' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                Cards
              </button>
            </div>
          </div>

          {loading ? (
            <TableSkeleton rows={6} columns={6} hasAvatar={true} />
          ) : apiError ? (
            <div className="api-error-state">
              <div className="api-error-state__icon">⚠️</div>
              <h3>Could not load practitioners</h3>
              <p>There was a problem connecting to the server. Please check your connection and try again.</p>
              <button className="btn btn-primary" onClick={fetchPractitioners}>Retry</button>
            </div>
          ) : (
            <table className="material-table practitioners-table">
              <colgroup>
                <col style={{ width: '28%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '26%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '6%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="material-cell material-cell--header">Practitioner</th>
                  <th className="material-cell material-cell--header">Specialization</th>
                  <th className="material-cell material-cell--header">Contact</th>
                  <th className="material-cell material-cell--header">Status</th>
                  <th className="material-cell material-cell--header">Experience</th>
                  <th className="material-cell material-cell--header actions-col" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
              {filteredPractitioners.map((practitioner) => (
                <tr key={practitioner.id} className="material-table-row">
                  <td className="material-cell">
                    <div className="material-location-cell">
                      <div className="material-avatar">
                        {practitioner.name ? practitioner.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'N/A'}
                      </div>
                      <div className="material-cell-info">
                        <h4>{practitioner.name || 'Unknown'}</h4>
                        <div className="material-cell-meta">{practitioner.title || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="material-cell">
                    <div className="material-cell-content">
                      <span className="material-body-medium">{practitioner.specialization}</span>
                      <span className="material-body-small material-text-secondary">{practitioner.qualifications}</span>
                    </div>
                  </td>
                  <td className="material-cell">
                    <div className="material-contact-info">
                      <div className="material-contact-primary">{practitioner.email}</div>
                      <div className="material-contact-secondary">{practitioner.phone}</div>
                    </div>
                  </td>
                  <td className="material-cell">
                    <button
                      className={`status-toggle-btn material-chip--${practitioner.status === 'active' ? 'success' : 'error'}`}
                      onClick={() => handleToggleStatus(practitioner.id)}
                      title={`Click to mark as ${practitioner.status === 'active' ? 'inactive' : 'active'}`}
                      disabled={submitting}
                    >
                      {practitioner.status === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="material-cell">
                    <span className="material-body-medium">{practitioner.experience}</span>
                  </td>
                  <td className="actions-cell">
                    <div className="action-icons">
                      <button
                        className="action-icon action-icon--view"
                        onClick={() => handleViewPractitioner(practitioner)}
                        data-tooltip="View Details"
                      ></button>
                      <button
                        className="action-icon action-icon--edit"
                        onClick={() => handleEditPractitioner(practitioner)}
                        data-tooltip="Edit Practitioner"
                      ></button>
                      <button
                        className="action-icon action-icon--delete"
                        onClick={() => handleDeletePractitioner(practitioner.id)}
                        data-tooltip="Delete Practitioner"
                      ></button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          )}

          {!loading && filteredPractitioners.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">🩺</div>
              <h3>{searchTerm || statusFilter !== 'all' ? 'No matches found' : 'No practitioners yet'}</h3>
              <p>{searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Add your first practitioner to get started managing your team.'}</p>
              {!searchTerm && statusFilter === 'all' && (
                <button onClick={handleAddPractitioner} className="btn btn-primary">
                  Add First Practitioner
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="material-cards-grid">
          {loading ? (
            <CardSkeleton cards={6} />
          ) : apiError ? (
            <div className="api-error-state">
              <div className="api-error-state__icon">⚠️</div>
              <h3>Could not load practitioners</h3>
              <p>There was a problem connecting to the server. Please check your connection and try again.</p>
              <button className="btn btn-primary" onClick={fetchPractitioners}>Retry</button>
            </div>
          ) : (
            filteredPractitioners.map((practitioner) => (
            <div key={practitioner.id} className="material-card">
              <div className="material-card-header">
                <h3 className="material-title-medium">{practitioner.name || 'Unknown'}</h3>
                <button
                  className={`status-toggle-btn material-chip material-chip--${practitioner.status === 'active' ? 'success' : 'error'}`}
                  onClick={() => handleToggleStatus(practitioner.id)}
                  title={`Click to mark as ${practitioner.status === 'active' ? 'inactive' : 'active'}`}
                  disabled={submitting}
                >
                  {practitioner.status === 'active' ? 'Active' : 'Inactive'}
                </button>
              </div>

              <div className="material-card-content">
                <div className="material-card-section">
                  <p className="material-body-medium">
                    {practitioner.specialization}<br/>
                    {practitioner.title}
                  </p>
                  <p className="material-body-small material-text-secondary">
                    📧 {practitioner.email}<br/>
                    📞 {practitioner.phone}
                  </p>
                </div>

                <div className="material-card-stats">
                  <div className="material-stat-item">
                    <span className="material-body-small material-text-secondary">Experience</span>
                    <span className="material-title-small">{practitioner.experience}</span>
                  </div>
                  <div className="material-stat-item">
                    <span className="material-body-small material-text-secondary">Join Date</span>
                    <span className="material-title-small">{practitioner.joinDate}</span>
                  </div>
                </div>

                <div className="material-chip-group">
                  {practitioner.qualifications && <span className="material-chip material-chip--info">🎓 {practitioner.qualifications}</span>}
                  {practitioner.location && <span className="material-chip material-chip--info">📍 {practitioner.location}</span>}
                </div>
              </div>

              <div className="material-card-actions">
                <button
                  className="material-button-outlined"
                  onClick={() => handleViewPractitioner(practitioner)}
                >
                  View Details
                </button>
                <button
                  className="material-button-filled"
                  onClick={() => handleEditPractitioner(practitioner)}
                >
                  Edit
                </button>
              </div>
            </div>
          )))}

          {!loading && filteredPractitioners.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">🩺</div>
              <h3>{searchTerm || statusFilter !== 'all' ? 'No matches found' : 'No practitioners yet'}</h3>
              <p>{searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Add your first practitioner to get started managing your team.'}</p>
              {!searchTerm && statusFilter === 'all' && (
                <button onClick={handleAddPractitioner} className="btn btn-primary">
                  Add First Practitioner
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <SlideoutPanel
        isOpen={isOpen}
        title={title}
        width={width}
        onClose={closeSlideout}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// Simple PractitionerDetail component
const PractitionerDetail = ({ practitioner, onEdit, onToggleStatus }) => (
  <div className="practitioner-detail">
    <div className="detail-header">
      <div className="practitioner-avatar large">
        {practitioner.name.split(' ').map(n => n[0]).join('')}
      </div>
      <div>
        <h2>{practitioner.name}</h2>
        <p>{practitioner.specialization}</p>
        <button
          className={`status-toggle-btn status-badge ${practitioner.status}`}
          onClick={onToggleStatus}
          title={`Click to mark as ${practitioner.status === 'active' ? 'inactive' : 'active'}`}
        >
          {practitioner.status}
        </button>
      </div>
    </div>

    <div className="detail-sections">
      <section>
        <h3>Contact Information</h3>
        <div className="detail-grid">
          <div className="detail-row">
            <span className="label">Email:</span>
            <span>{practitioner.email}</span>
          </div>
          <div className="detail-row">
            <span className="label">Phone:</span>
            <span>{practitioner.phone}</span>
          </div>
          <div className="detail-row">
            <span className="label">Location:</span>
            <span>{practitioner.location}</span>
          </div>
        </div>
      </section>

      <section>
        <h3>Professional Information</h3>
        <div className="detail-grid">
          <div className="detail-row">
            <span className="label">Qualifications:</span>
            <span>{practitioner.qualifications}</span>
          </div>
          <div className="detail-row">
            <span className="label">Experience:</span>
            <span>{practitioner.experience}</span>
          </div>
          <div className="detail-row">
            <span className="label">Join Date:</span>
            <span>{practitioner.joinDate}</span>
          </div>
        </div>
      </section>

      {practitioner.bio && (
        <section>
          <h3>Biography</h3>
          <p>{practitioner.bio}</p>
        </section>
      )}

      <div className="detail-actions">
        <button className="primary-button" onClick={onEdit}>
          Edit Practitioner
        </button>
      </div>
    </div>
  </div>
);

export default PractitionersView;